import {
  ClockIcon,
  DocumentTextIcon,
  PencilIcon as PencilIconOutline,
  TrashIcon,
  ViewListIcon as ViewListIconOutline,
  XIcon,
} from "@heroicons/react/outline";
import {
  ClockIcon as ClockIconSolid,
  PencilIcon as PencilIconSolid,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import posthog from "posthog-js";
import { observable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import WorkflowCanvas from "../WorkflowCanvas";
import { IWorkflow } from "../WorkflowCanvas/workflow-store";

import OverviewContent from "./OverviewContent";
import { useTestRunModal } from "./hooks/TestRunModalWrapper";
import useWorkflowData from "./hooks/useWorkflowData";

import Changelog from "@/components/Changelog";
import { FolderScopeProvider } from "@/components/ChooseEntityDialog/folder-scope-context-provider";
import SaveWithCommitButton from "@/components/SaveWithCommitButton";
import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import { useDeleteEntity } from "@/components/UnifiedRegistry/Dialogs/hooks/useDeleteEntity";
import useFolderPathBreadcrumbs from "@/components/UnifiedRegistry/hooks/useFolderPathBreadcrumbs";
import { ResourceHeader } from "@/components/ui/ResourceHeader";
import { TabItem } from "@/components/ui/TabNavigation/types";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useNavigateWithSearch } from "@/hooks";
import { ToastType } from "@/enums";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType, useGetChangelogPendingCount } from "@/queries";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayToast } from "@/utils/toast";

interface WorkflowProps {
  editing?: boolean;
  defaultTab?: "overview" | "editor" | "changelog";
}

const Workflow = observer(({ editing, defaultTab }: WorkflowProps) => {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current) {
      posthog.capture("Workflow Page Viewed", {
        name: "Workflow",
        href: window.location.href,
      });
      hasTracked.current = true;
    }
  }, []);

  const { isEditDisabled } = useRbac();
  const cannotEditWorkflow = isEditDisabled(EntityType.WORKFLOW);

  const auth = useAuth();
  const userToken = auth?.userToken || "";
  const {
    workflowId,
    versionId: versionNumber,
    workspaceId,
  } = useParams<{
    workflowId: string;
    versionId: string;
    workspaceId: string;
  }>();

  const [showVersionList, setShowVersionList] = useState(!editing);

  const getInitialTabIndex = () => {
    if (defaultTab === "editor") {
      return 1;
    }
    if (defaultTab === "changelog") {
      return 2;
    }
    return 0;
  };

  const [selectedTabIndex, setSelectedTabIndex] = useState(getInitialTabIndex());
  const navigateWithSearch = useNavigateWithSearch();
  const hasRedirectedRef = useRef(false);

  // Reset tab to overview when navigating to base version route (e.g., from changelog)
  useEffect(() => {
    if (!defaultTab) {
      setSelectedTabIndex(0);
    } else if (defaultTab === "editor") {
      setSelectedTabIndex(1);
    } else if (defaultTab === "changelog") {
      setSelectedTabIndex(2);
    }
  }, [defaultTab, versionNumber]);

  // Redirect to overview tab if user tries to access editor without permission
  useEffect(() => {
    if (defaultTab === "editor" && cannotEditWorkflow && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      navigateWithSearch(
        `/workspace/${workspaceId}/workflow/${workflowId}/version/${versionNumber}`,
        {
          replace: true,
        },
      );
      // Show toast after navigation
      setTimeout(() => {
        displayToast("You don't have access to Workflow Editor page", ToastType.warning);
      }, 0);
    }
  }, [defaultTab, cannotEditWorkflow, navigateWithSearch, workspaceId, workflowId, versionNumber]);

  const { deleteEntity } = useDeleteEntity();

  const handleTabChange = useCallback(
    (index: number) => {
      setSelectedTabIndex(index);

      if (index === 1) {
        navigateWithSearch(
          `/workspace/${workspaceId}/workflow/${workflowId}/version/${versionNumber}/playground`,
          { replace: true },
        );
      } else if (index === 2) {
        navigateWithSearch(
          `/workspace/${workspaceId}/workflow/${workflowId}/version/${versionNumber}/changelog`,
          { replace: true },
        );
      } else {
        navigateWithSearch(
          `/workspace/${workspaceId}/workflow/${workflowId}/version/${versionNumber}`,
          {
            replace: true,
          },
        );
      }
    },
    [workspaceId, workflowId, versionNumber, navigateWithSearch],
  );

  const workflowRef = useRef<IWorkflow>();
  const observableWorkflowRef = observable.box(workflowRef.current);

  const { data: pendingChangelogCount } = useGetChangelogPendingCount({
    entityId: Number(workflowId),
    entityType: EntityType.WORKFLOW,
    enabled: !!workflowId,
  });

  const { workflow, versions, isLoading, refetchVersions } = useWorkflowData(
    {
      workflowId: workflowId!,
      versionNumber: versionNumber!,
      userToken,
      workspaceId: workspaceId!,
    },
    !!editing,
    navigateWithSearch,
  );

  // Sync the in-memory workflow store's name when the underlying details
  // change from elsewhere (e.g. sidebar rename), so the title reflects it.
  useEffect(() => {
    const store = workflowRef.current;
    if (store && workflow.name && store.name !== workflow.name) {
      runInAction(() => {
        store.setName(workflow.name || "", false);
      });
    }
  }, [workflow.name]);

  const { workflow: editableWorkflow } = useWorkflowData(
    {
      workflowId: workflowId!,
      versionNumber: versionNumber!,
      userToken,
      workspaceId: workspaceId!,
    },
    true, // Always request editable data for the Editor tab
    navigateWithSearch,
  );

  const { modal: testRunModal, buttonAction: testRunButton } = useTestRunModal({
    workflowId: workflowId!,
    workflowName: workflow.name || "",
    versionNumber: versionNumber!,
    workspaceId: workspaceId!,
    inputVariables: workflowRef.current?.input_variables || [],
  });

  const handleDelete = useCallback(async () => {
    const entity = {
      id: Number(workflow.workflow_id),
      name: workflow.name || "",
      type: EntityType.WORKFLOW,
      path: [],
    };

    await deleteEntity(entity, () => {
      navigateWithSearch(`/workspace/${workspaceId}/home`);
    });
  }, [deleteEntity, workflow.workflow_id, workflow.name, navigateWithSearch, workspaceId]);

  const handleGoToAgentBuilder = useCallback(() => {
    handleTabChange(1); // Switch to Editor tab (index 1)
  }, [handleTabChange]);

  const agentBuilderButton = useMemo(
    () => ({
      onClick: handleGoToAgentBuilder,
      variant: "outline" as const,
      icon: <PencilIcon className="mr-1.5 h-4 w-4" />,
      label: "Edit",
    }),
    [handleGoToAgentBuilder],
  );

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        id: 0,
        name: "Overview",
        outlineIcon: <ViewListIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        content: (
          <OverviewContent
            showVersionList={showVersionList}
            versions={versions!}
            workflowRef={workflowRef}
            observableWorkflowRef={observableWorkflowRef}
            setShowVersionList={setShowVersionList}
            refetchVersions={refetchVersions}
            userToken={userToken}
            workflow={workflow as IWorkflow}
            workflowId={workflowId!}
            versionNumber={versionNumber!}
            agentBuilderButton={agentBuilderButton}
          />
        ),
      },
      {
        id: 1,
        name: "Editor",
        disabled: cannotEditWorkflow,
        tooltipContent: cannotEditWorkflow
          ? "You don't have permission to edit this workflow"
          : undefined,
        outlineIcon: <PencilIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        solidIcon: <PencilIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        content: (
          <div className="mt-2 flex h-full w-full">
            <FolderScopeProvider value={workflow.folder_id}>
              <WorkflowCanvas
                ref={ref => {
                  if (ref) {
                    workflowRef.current = ref as IWorkflow;
                    runInAction(() => observableWorkflowRef.set(ref as IWorkflow));
                  }
                }}
                {...editableWorkflow}
                readonly={false}
                userToken={userToken}
              />
            </FolderScopeProvider>
          </div>
        ),
      },
      {
        id: 2,
        name: "Changelog",
        outlineIcon: <ClockIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        solidIcon: <ClockIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        count: pendingChangelogCount,
        content: (
          <Changelog
            entity={{
              id: Number(workflowId),
              type: EntityType.WORKFLOW,
            }}
          />
        ),
      },
    ],
    [
      showVersionList,
      versions,
      workflowRef,
      observableWorkflowRef,
      setShowVersionList,
      refetchVersions,
      userToken,
      workflow,
      editableWorkflow,
      workflowId,
      versionNumber,
      agentBuilderButton,
      cannotEditWorkflow,
      pendingChangelogCount,
    ],
  );

  const handleTitleEdit = useCallback(
    ({ newTitle }: { newTitle: string }) => {
      const currentWorkflow = observableWorkflowRef.get();
      if (currentWorkflow) {
        runInAction(() => {
          currentWorkflow.setName(newTitle, true);
        });
      }
    },
    [observableWorkflowRef],
  );

  const queryClient = useQueryClient();

  const updateWorkflowTags = useMutation(
    (tags: string[]) =>
      fetch(`${ENDPOINTS.workflows}/${workflowId}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ tags }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.workflows, workflowId]);
      },
    },
  );

  const handleTagsEdit = useCallback(
    (tags: string[]) => {
      updateWorkflowTags.mutate(tags);
    },
    [updateWorkflowTags],
  );

  const breadcrumbProps = useFolderPathBreadcrumbs(
    workflow.folder_id ?? null,
    observableWorkflowRef.get()?.name || workflow.name || "",
  );

  return (
    <div className="flex h-full flex-col">
      <BreadcrumbsPageWrapper
        {...breadcrumbProps}
        className="flex h-full flex-col pb-3"
        entityTitle={{
          type: EntityType.WORKFLOW,
          title: observableWorkflowRef.get()?.name || workflow.name || "",
          onTitleEdit: handleTitleEdit,
          isLoading,
        }}
      >
        <ResourceHeader
          type={EntityType.WORKFLOW}
          titleInBreadcrumb
          title={observableWorkflowRef.get()?.name || workflow.name || ""}
          onTitleEdit={handleTitleEdit}
          tags={workflow.tags || []}
          onTagsEdit={handleTagsEdit}
          actions={[
            {
              label: "Delete",
              onClick: handleDelete,
              variant: "destructiveLink",
              disabled: cannotEditWorkflow,
              tooltipContent: cannotEditWorkflow
                ? "You don't have permission to delete this workflow"
                : undefined,
              icon: <TrashIcon className="mr-2 h-4 w-4" />,
            },
          ]}
          id={workflow.workflow_id!}
          createdAt={workflow.created_at || ""}
          versionInfo={{
            current: Number(versionNumber) || 1,
            total: versions?.length || 0,
          }}
          minimal
          isLoading={isLoading}
          showVersions={showVersionList}
          onToggleVersions={() => setShowVersionList(!showVersionList)}
          tabsProps={{
            tabs: tabs,
            actionButtons: (
              <div className="relative bottom-1 flex gap-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
                  onClick={() =>
                    window.open("https://docs.promptlayer.com/why-promptlayer/agents", "_blank")
                  }
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  <span className="text-sm">Docs</span>
                </Button>
                {selectedTabIndex === 0 ? (
                  // Overview tab - show Test Run button
                  <Button
                    onClick={testRunButton.onClick}
                    variant={testRunButton.variant}
                    size="sm"
                    aria-label="Test Run"
                  >
                    {testRunButton.icon}
                    {testRunButton.label}
                  </Button>
                ) : (
                  // Editor tab - show Cancel Edit and Save as New Version
                  selectedTabIndex === 1 && (
                    <>
                      <Button
                        variant="destructiveOutline"
                        size="sm"
                        onClick={() => handleTabChange(0)} // Switch back to Overview tab
                        className="flex items-center gap-2"
                      >
                        <XIcon className="h-4 w-4" />
                        Cancel Edit
                      </Button>
                      <BasicTooltip
                        content="You don't have permission to save this workflow"
                        shouldShow={cannotEditWorkflow}
                      >
                        <span>
                          <SaveWithCommitButton
                            name="Save as New Version"
                            disabled={cannotEditWorkflow}
                            onSave={async commitMessage => {
                              if (!workflowRef.current) {
                                throw new Error(
                                  "Workflow reference is not available. Make sure you're in the Editor tab.",
                                );
                              }

                              const response = await workflowRef.current.save(commitMessage, true);

                              await Promise.all([
                                queryClient.invalidateQueries([
                                  "changelog",
                                  workspaceId,
                                  workflow.workflow_id,
                                  "WORKFLOW",
                                ]),
                                queryClient.invalidateQueries(["global-changelog", workspaceId]),
                                refetchVersions(),
                              ]);

                              const nextVersionId =
                                response.workflow_version_id ?? Number(versionNumber) + 1;
                              const nextPath = `/workspace/${workspaceId}/workflow/${workflowId}/version/${nextVersionId}`;

                              return () => {
                                handleTabChange(0);
                                navigateWithSearch(nextPath, { replace: true });
                              };
                            }}
                          />
                        </span>
                      </BasicTooltip>
                    </>
                  )
                )}
              </div>
            ),
            selectedIndex: selectedTabIndex,
            handleTabChange: handleTabChange,
            isUrlBased: false,
          }}
        />
      </BreadcrumbsPageWrapper>

      {testRunModal}
    </div>
  );
});

export default Workflow;
